import { useState, useEffect } from 'react'

const API = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}`

function AdminFinanceiro() {
    const [resumo, setResumo] = useState(null)
    const [transacoes, setTransacoes] = useState([])
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState('')
    const [dataInicio, setDataInicio] = useState('')
    const [dataFim, setDataFim] = useState('')
    const [pagina, setPagina] = useState(1)
    const [totalPaginas, setTotalPaginas] = useState(1)
    const [carregandoMais, setCarregandoMais] = useState(false)
    const [selectedTransaction, setSelectedTransaction] = useState(null)
    const [periodoPreset, setPeriodoPreset] = useState('tudo')

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
    }

    useEffect(() => {
        carregarDados()
    }, [])

    const carregarDados = async () => {
        setLoading(true)
        setErro('')
        try {
            await Promise.all([
                fetchResumo(null, null),
                fetchTransacoes(1, false, null, null)
            ])
        } catch (error) {
            console.error('Erro:', error)
            setErro('Não foi possível carregar os dados financeiros.')
        } finally {
            setLoading(false)
        }
    }

    const fetchResumo = async (startOverride = null, endOverride = null) => {
        try {
            const params = new URLSearchParams()
            const sDate = startOverride !== null ? startOverride : dataInicio
            const eDate = endOverride !== null ? endOverride : dataFim
            
            if (sDate) params.append('dataInicio', sDate)
            if (eDate) params.append('dataFim', eDate)

            const res = await fetch(`${API}/api/admin/financeiro/resumo?${params}`, { headers })
            if (res.ok) {
                const data = await res.json()
                setResumo(data)
            }
        } catch (error) {
            console.error('Erro ao carregar resumo financeiro:', error)
        }
    }

    const fetchTransacoes = async (pageNum = 1, append = false, startOverride = null, endOverride = null) => {
        try {
            // Se for a primeira página (não "carregar mais"), atualiza também os KPIs para o período correspondente
            if (pageNum === 1) {
                fetchResumo(startOverride, endOverride)
            }

            const params = new URLSearchParams()
            params.append('page', pageNum)
            params.append('limit', 20)
            
            const sDate = startOverride !== null ? startOverride : dataInicio
            const eDate = endOverride !== null ? endOverride : dataFim
            
            if (sDate) params.append('dataInicio', sDate)
            if (eDate) params.append('dataFim', eDate)

            const res = await fetch(`${API}/api/admin/financeiro/transacoes?${params}`, { headers })
            if (res.ok) {
                const data = await res.json()
                const novasTransacoes = data.transacoes || []
                
                if (append) {
                    setTransacoes(prev => [...prev, ...novasTransacoes])
                } else {
                    setTransacoes(novasTransacoes)
                }
                
                setPagina(data.page || 1)
                setTotalPaginas(data.totalPages || 1)
            }
        } catch (error) {
            console.error('Erro ao carregar transações:', error)
        }
    }

    const handleFiltrar = () => {
        fetchTransacoes(1, false)
    }

    const handleCarregarMais = async () => {
        setCarregandoMais(true)
        const proximaPagina = pagina + 1
        await fetchTransacoes(proximaPagina, true)
        setCarregandoMais(false)
    }

    const aplicarPreset = (preset) => {
        setPeriodoPreset(preset)
        
        const formatISODate = (d) => {
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        const hoje = new Date()
        let inicio = ''
        let fim = ''

        if (preset === 'hoje') {
            inicio = formatISODate(hoje)
            fim = formatISODate(hoje)
        } else if (preset === 'ontem') {
            const ontem = new Date()
            ontem.setDate(hoje.getDate() - 1)
            inicio = formatISODate(ontem)
            fim = formatISODate(ontem)
        } else if (preset === '7d') {
            const seteDias = new Date()
            seteDias.setDate(hoje.getDate() - 6)
            inicio = formatISODate(seteDias)
            fim = formatISODate(hoje)
        } else if (preset === '14d') {
            const quatorzeDias = new Date()
            quatorzeDias.setDate(hoje.getDate() - 13)
            inicio = formatISODate(quatorzeDias)
            fim = formatISODate(hoje)
        } else if (preset === '30d') {
            const trintaDias = new Date()
            trintaDias.setDate(hoje.getDate() - 29)
            inicio = formatISODate(trintaDias)
            fim = formatISODate(hoje)
        }

        setDataInicio(inicio)
        setDataFim(fim)
        
        fetchTransacoes(1, false, inicio, fim)
    }

    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
    }

    const formatDate = (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '—'

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="spinner"></div>
                <span>Carregando dados financeiros...</span>
            </div>
        )
    }

    const kpis = [
        { icon: '💰', value: formatPrice(resumo?.receitaTotal || 0), label: 'Receita Aprovada (Pago)' },
        { icon: '⏳', value: formatPrice(resumo?.receitaPendente || 0), label: 'Receita Pendente' },
        { icon: '📅', value: formatPrice(resumo?.receitaMes || 0), label: 'Receita Aprovada no Mês' },
        { icon: '🎫', value: formatPrice(resumo?.ticketMedio || 0), label: 'Ticket Médio (Pago)' },
        { icon: '🔢', value: resumo?.totalTransacoes || 0, label: 'Transações Pagas' },
        { icon: '⚠️', value: resumo?.totalPendentes || 0, label: 'Transações Pendentes' },
    ]

    return (
        <div>
            <div className="admin-section-header" style={{ marginBottom: '1.5rem' }}>
                <h3>💰 Financeiro</h3>
            </div>

            {erro && <div className="admin-error">{erro}</div>}

            {/* KPIs */}
            <div className="admin-kpi-grid finance-kpi-grid">
                {kpis.map((kpi, i) => (
                    <div key={i} className="admin-kpi-card">
                        <div className="admin-kpi-icon">{kpi.icon}</div>
                        <div className="admin-kpi-value" style={{ fontSize: typeof kpi.value === 'string' ? '1.5rem' : '2rem' }}>
                            {typeof kpi.value === 'number' ? kpi.value.toLocaleString('pt-BR') : kpi.value}
                        </div>
                        <div className="admin-kpi-label">{kpi.label}</div>
                    </div>
                ))}
            </div>

            {/* Transações */}
            <div className="admin-section">
                <div className="admin-section-header">
                    <h3>📋 Transações</h3>
                    <div className="admin-date-range" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                        <select 
                            value={periodoPreset} 
                            onChange={(e) => aplicarPreset(e.target.value)}
                            style={{ 
                                background: '#1e293b', 
                                border: '1px solid rgba(255,255,255,0.1)', 
                                borderRadius: '6px', 
                                color: '#fff', 
                                padding: '0.45rem 1rem', 
                                fontSize: '0.85rem',
                                outline: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="tudo">Todos os Períodos 📅</option>
                            <option value="hoje">Hoje ⏳</option>
                            <option value="ontem">Ontem ⏰</option>
                            <option value="7d">Últimos 7 dias 📆</option>
                            <option value="14d">Últimos 14 dias 📅</option>
                            <option value="30d">Últimos 30 dias 🗓️</option>
                            <option value="personalizado">Personalizado 🛠️</option>
                        </select>

                        <label style={{ fontSize: '0.85rem', color: '#94a3b8' }}>De:</label>
                        <input
                            type="date"
                            value={dataInicio}
                            onChange={(e) => {
                                setPeriodoPreset('personalizado');
                                setDataInicio(e.target.value);
                            }}
                            style={{
                                background: '#1e293b',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '6px',
                                color: '#fff',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.85rem',
                                outline: 'none'
                            }}
                        />
                        <label style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Até:</label>
                        <input
                            type="date"
                            value={dataFim}
                            onChange={(e) => {
                                setPeriodoPreset('personalizado');
                                setDataFim(e.target.value);
                            }}
                            style={{
                                background: '#1e293b',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '6px',
                                color: '#fff',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.85rem',
                                outline: 'none'
                            }}
                        />
                        <button className="admin-action-btn success" onClick={handleFiltrar} style={{ padding: '0.45rem 1.25rem', fontSize: '0.85rem' }}>
                            Filtrar
                        </button>
                    </div>
                </div>

                {transacoes.length > 0 ? (
                    <>
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th className="hide-mobile">ID</th>
                                        <th>Usuário</th>
                                        <th className="hide-tablet hide-mobile">Descrição</th>
                                        <th>Valor</th>
                                        <th className="hide-mobile">Método</th>
                                        <th>Status</th>
                                        <th className="hide-mobile">Data</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transacoes.map((t, i) => (
                                        <tr key={t.id || i} onClick={() => setSelectedTransaction(t)} style={{ cursor: 'pointer' }} className="admin-clickable-row">
                                            <td data-label="ID" className="hide-mobile" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {t.id ? `#${String(t.id).slice(-6)}` : '—'}
                                            </td>
                                            <td data-label="Usuário" style={{ color: '#f8fafc', fontWeight: 500 }}>
                                                {t.usuario_nome || t.usuarioNome || '—'}
                                            </td>
                                            <td data-label="Descrição" className="hide-tablet hide-mobile">{t.descricao || t.tipo || '—'}</td>
                                            <td data-label="Valor" style={{ fontWeight: 600, color: 'var(--color-primary-light)' }}>
                                                {formatPrice(t.valor)}
                                            </td>
                                            <td data-label="Método" className="hide-mobile">{t.metodo || t.metodoPagamento || '—'}</td>
                                            <td data-label="Status">
                                                {(() => {
                                                    const s = String(t.status || '').toLowerCase();
                                                    if (s === 'aprovado' || s === 'pago' || s === 'paga') {
                                                        return <span className="admin-badge admin-badge-ativo">Pago</span>;
                                                    }
                                                    if (s === 'pendente') {
                                                        return <span className="admin-badge admin-badge-pendente">Pendente</span>;
                                                    }
                                                    if (s === 'cortesia') {
                                                        return <span className="admin-badge admin-badge-admin">Cortesia 🎁</span>;
                                                    }
                                                    return <span className="admin-badge admin-badge-expirado">{t.status || '—'}</span>;
                                                })()}
                                            </td>
                                            <td data-label="Data" className="hide-mobile">{formatDate(t.criado_em || t.criadoEm)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {pagina < totalPaginas && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                                <button 
                                    className="admin-action-btn success" 
                                    onClick={handleCarregarMais}
                                    disabled={carregandoMais}
                                    style={{ padding: '0.65rem 2.5rem', fontSize: '0.85rem', borderRadius: '30px', fontWeight: '600', transition: 'all 0.2s' }}
                                >
                                    {carregandoMais ? 'Carregando transações...' : 'Carregar Mais ➕'}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="admin-empty">
                        <div className="empty-icon">💳</div>
                        <p>Nenhuma transação encontrada para o período selecionado.</p>
                    </div>
                )}
            </div>

            {/* Modal de Detalhes da Transação */}
            {selectedTransaction && (
                <div className="admin-modal-overlay" onClick={() => setSelectedTransaction(null)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
                        <div className="admin-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#fff' }}>🔍 Detalhes da Transação</h3>
                            <button className="admin-close-btn" onClick={() => setSelectedTransaction(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', outline: 'none' }}>×</button>
                        </div>
                        
                        <div className="admin-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                            <div>
                                <h4 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.4rem', marginBottom: '0.6rem', fontSize: '0.95rem' }}>👤 Dados do Usuário</h4>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Nome:</strong> {selectedTransaction.usuario_nome || '—'}</p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Email:</strong> {selectedTransaction.usuario_email || '—'}</p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>ID Usuário:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedTransaction.usuario_id || '—'}</span></p>
                            </div>
                            
                            <div>
                                <h4 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.4rem', marginBottom: '0.6rem', fontSize: '0.95rem' }}>📦 Detalhes do Plano</h4>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Produto:</strong> {selectedTransaction.descricao || '—'}</p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Plano SQL:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedTransaction.plano || '—'}</span></p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Valor:</strong> <span style={{ color: 'var(--color-primary-light)', fontWeight: 600 }}>{formatPrice(selectedTransaction.valor)}</span></p>
                            </div>
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                            <div style={{ background: 'rgba(30, 41, 59, 0.4)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <h4 style={{ color: '#fff', marginBottom: '0.5rem', fontSize: '0.95rem' }}>⚙️ Transação & Auditoria</h4>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>ID Assinatura:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedTransaction.id || '—'}</span></p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Gateway ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedTransaction.gateway_id || '—'}</span></p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Origem:</strong> {String(selectedTransaction.gateway_id).toLowerCase() === 'admin' ? 'Ativado por Admin 🛠️' : 'Mercado Pago 💳'}</p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Status:</strong> 
                                    <span style={{ marginLeft: '8px' }}>
                                        {(() => {
                                            const s = String(selectedTransaction.status || '').toLowerCase();
                                            if (s === 'aprovado' || s === 'pago' || s === 'paga') {
                                                return <span className="admin-badge admin-badge-ativo">Pago</span>;
                                            }
                                            if (s === 'pendente') {
                                                return <span className="admin-badge admin-badge-pendente">Pendente</span>;
                                            }
                                            if (s === 'cortesia') {
                                                return <span className="admin-badge admin-badge-admin">Cortesia 🎁</span>;
                                            }
                                            return <span className="admin-badge admin-badge-expirado">{selectedTransaction.status || '—'}</span>;
                                        })()}
                                    </span>
                                </p>
                                <p style={{ margin: '0.4rem 0' }}><strong style={{ color: '#f8fafc' }}>Data de Registro:</strong> {new Date(selectedTransaction.criado_em).toLocaleString('pt-BR')}</p>
                                
                                {selectedTransaction.url_pagamento && (
                                    <p style={{ margin: '0.6rem 0 0 0', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.6rem' }}>
                                        <strong style={{ color: '#f8fafc' }}>Nota / URL de Pagamento:</strong>{' '}
                                        {selectedTransaction.url_pagamento.startsWith('http') ? (
                                            <a href={selectedTransaction.url_pagamento} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-light)', textDecoration: 'underline', fontWeight: '500' }}>
                                                Link de Pagamento 🔗
                                            </a>
                                        ) : (
                                            <span style={{ color: '#e2e8f0', fontStyle: 'italic', background: 'rgba(255, 255, 255, 0.05)', padding: '2px 8px', borderRadius: '4px' }}>{selectedTransaction.url_pagamento}</span>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="admin-modal-footer" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
                            <button className="admin-action-btn success" onClick={() => setSelectedTransaction(null)} style={{ padding: '0.6rem 2rem', borderRadius: '20px', fontWeight: 600 }}>
                                Fechar Detalhes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminFinanceiro
